import type { AxiosInstance } from "axios";
import type {
  FetchPropertyAuditsResult,
  PropertyAuditAuthor,
  PropertyAuditEntry,
  PropertyAuditEvent,
} from "../types/propertyAudit";

export const PROPERTY_AUDITS_PER_PAGE = 20;

type ApiPropertyAudit = {
  id?: number | string;
  property_id?: number | string;
  author_id?: number | string | null;
  field?: string;
  field_label?: string | null;
  source?: string | null;
  source_label?: string | null;
  old_value?: string | null;
  new_value?: string | null;
  created_at?: string;
  author?: {
    id?: number | string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  } | null;
  event?: {
    tenant?: string | null;
    consultant?: string | null;
    reason?: string | null;
    final_rent_value?: string | null;
    rented_by?: string | null;
  } | null;
};

function mapAuthor(raw?: ApiPropertyAudit["author"]): PropertyAuditAuthor | null {
  if (!raw || raw.id == null) return null;

  return {
    id: Number(raw.id),
    first_name: raw.first_name ?? null,
    last_name: raw.last_name ?? null,
    email: raw.email ?? null,
  };
}

function mapEvent(raw?: ApiPropertyAudit["event"]): PropertyAuditEvent | null {
  if (!raw || typeof raw !== "object") return null;

  const event: PropertyAuditEvent = {
    tenant: raw.tenant ?? null,
    consultant: raw.consultant ?? null,
    reason: raw.reason ?? null,
    final_rent_value: raw.final_rent_value ?? null,
    rented_by: raw.rented_by ?? null,
  };

  return Object.values(event).some(Boolean) ? event : null;
}

function mapAudit(raw: ApiPropertyAudit): PropertyAuditEntry {
  return {
    id: Number(raw.id),
    property_id: Number(raw.property_id),
    author_id: raw.author_id != null ? Number(raw.author_id) : null,
    field: String(raw.field ?? ""),
    field_label: raw.field_label ?? null,
    source: raw.source ?? null,
    source_label: raw.source_label ?? null,
    old_value: raw.old_value ?? null,
    new_value: raw.new_value ?? null,
    created_at: String(raw.created_at ?? ""),
    author: mapAuthor(raw.author),
    event: mapEvent(raw.event),
  };
}

type PaginatedAuditsPayload = {
  current_page?: number;
  last_page?: number;
  total?: number;
  per_page?: number;
  data?: ApiPropertyAudit[];
};

function extractAuditList(payload: unknown): ApiPropertyAudit[] {
  if (Array.isArray(payload)) return payload;

  if (!payload || typeof payload !== "object") return [];

  const root = payload as {
    data?: unknown;
    audits?: unknown;
  };

  if (Array.isArray(root.data)) return root.data;

  const audits = root.audits;
  if (Array.isArray(audits)) return audits;

  if (audits && typeof audits === "object") {
    const paginated = audits as PaginatedAuditsPayload;
    if (Array.isArray(paginated.data)) return paginated.data;
  }

  return [];
}

function resolvePaginatedPayload(payload: unknown): PaginatedAuditsPayload {
  if (!payload || typeof payload !== "object") return {};

  const root = payload as {
    current_page?: number;
    last_page?: number;
    total?: number;
    per_page?: number;
    audits?: PaginatedAuditsPayload;
  };

  if (root.audits && typeof root.audits === "object") {
    return root.audits;
  }

  return root;
}

function resolveLastPage(payload: PaginatedAuditsPayload): number {
  if (payload.last_page != null) return Math.max(0, Number(payload.last_page));
  if (payload.total != null && payload.per_page) {
    return Math.max(0, Math.ceil(payload.total / payload.per_page));
  }
  return 0;
}

export async function fetchPropertyAudits(
  client: AxiosInstance,
  propertyId: number,
  page = 1,
  perPage = PROPERTY_AUDITS_PER_PAGE
): Promise<FetchPropertyAuditsResult> {
  const response = await client.get(`/api/adm/properties/${propertyId}/audits`, {
    params: { page, per_page: perPage },
  });

  const payload = response.data?.data ?? response.data ?? {};
  const paginated = resolvePaginatedPayload(payload);
  const audits = extractAuditList(payload).map(mapAudit);

  return {
    audits,
    currentPage: Number(paginated.current_page ?? page) || page,
    lastPage: resolveLastPage(paginated),
    total: Number(paginated.total ?? audits.length) || audits.length,
  };
}
